// Der Hinweis, der in der Buchungsstrecke AN DIE STELLE des Kalenders tritt,
// solange die Online-Buchung wegen Urlaub/Betriebsferien gesperrt ist.
//
// Anders als das Band im Kopf (ClosureBanner) ist das hier der Inhalt, den der
// Besucher an genau der Stelle vorfindet, an der er buchen wollte — deshalb
// deutlicher und mit den Kontaktwegen als Handlungsangebot.
//
// Zwei Paletten, ein Text: die Website läuft auf navy/coral, das SmartAI-Panel
// auf den ds-*-Tokens. Ein gemeinsamer Baustein hält die Aussage identisch,
// ohne dass eine der beiden Oberflächen fremd aussieht.

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatLongDate } from '../utils/formatLongDate';
import { PHONE_DISPLAY, PHONE_HREF, WHATSAPP_HREF } from '../config/contact';

interface Props {
  /** Letzter gesperrter Tag, 'YYYY-MM-DD' (einschließlich). */
  until: string;
  /** 'site' = Website-Wizard, 'panel' = SmartAI-Panel (enger, ds-Palette). */
  variant?: 'site' | 'panel';
}

const STYLES = {
  site: {
    box: 'rounded-2xl border border-coral/30 bg-coral/5 p-5 sm:p-6',
    badge: 'bg-coral/15 text-coral',
    title: 'text-navy text-lg',
    body: 'text-navy/75',
    primary: 'bg-coral text-white hover:bg-coral-dark',
    secondary: 'border border-coral/40 text-navy hover:bg-coral/10',
  },
  panel: {
    box: 'rounded-2xl border border-ds-outline-variant bg-ds-surface-container-low p-4',
    badge: 'bg-ds-secondary text-white',
    title: 'text-ds-primary text-[15.5px]',
    body: 'text-ds-on-surface-variant',
    primary: 'bg-ds-secondary text-white hover:opacity-90',
    secondary: 'border border-ds-outline-variant text-ds-primary hover:bg-ds-surface-container',
  },
} as const;

// Fallbacks wie im SmartAI-Panel: ein ausgeliefertes Bundle mit älteren
// Übersetzungen darf an dieser Stelle nicht weiß werden — der Hinweis ist
// genau dann wichtig, wenn sonst nichts mehr zu buchen ist.
const FALLBACK = {
  bookingTitle: 'Online-Buchung pausiert',
  bookingText: 'Bis einschließlich {date} können hier keine Termine gebucht werden. Melden Sie sich gern telefonisch oder per WhatsApp.',
  call: 'Anrufen',
  whatsapp: 'WhatsApp',
};

const ClosureNotice: React.FC<Props> = ({ until, variant = 'site' }) => {
  const { translations, language } = useLanguage();
  const t = { ...FALLBACK, ...(translations?.closure || {}) };
  const s = STYLES[variant];
  const date = formatLongDate(until, language);

  return (
    <div role="status" data-testid="closure-notice" className={s.box}>
      <div className="flex items-start gap-3">
        <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center ${s.badge}`}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M3 11h18M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
          </svg>
        </span>
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold ${s.title}`}>{t.bookingTitle}</h3>
          <p className={`text-sm leading-relaxed mt-1 ${s.body}`}>
            {t.bookingText.replace('{date}', date)}
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <a
          href={PHONE_HREF}
          className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-center no-underline transition-colors ${s.primary}`}
        >
          {t.call} · {PHONE_DISPLAY}
        </a>
        <a
          href={WHATSAPP_HREF}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-center no-underline transition-colors ${s.secondary}`}
        >
          {t.whatsapp}
        </a>
      </div>
    </div>
  );
};

export default ClosureNotice;
